import { Link, NavLink, useLocation } from "react-router-dom";
import { IoMdMenu, IoMdClose } from "react-icons/io";
import { FaChevronRight } from "react-icons/fa";
import LogoIcon from "../../assets/images/logo-icon.png";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const showTooltip = (e, text) => {
  const tooltip = document.createElement("div");
  tooltip.className = "menu-tooltip";
  tooltip.innerText = text;
  document.body.appendChild(tooltip);

  const rect = e.currentTarget.getBoundingClientRect();
  tooltip.style.top = `${rect.top + rect.height / 2}px`;
  tooltip.style.left = `${rect.right}px`;
  tooltip.style.transform = "translateX(14px) translateY(-50%)";
  tooltip.style.opacity = "1";

  e.currentTarget._tooltip = tooltip;
};

const hideTooltip = (e) => {
  const tooltip = e.currentTarget._tooltip;
  if (tooltip) tooltip.remove();
};

// Recursively find which dropdown paths should be open for the current route
const getInitialOpen = (items, pathname, parentPath = "") => {
  const result = {};
  for (const item of items) {
    if (item.type !== "dropdown" || !item.children) continue;
    const itemPath = parentPath ? `${parentPath}/${item.text}` : item.text;
    const childResult = getInitialOpen(item.children, pathname, itemPath);
    const hasActiveChild =
      Object.values(childResult).some(Boolean) ||
      item.children.some(
        (c) => c.type === "link" && c.to && pathname.startsWith(c.to),
      );
    if (hasActiveChild) result[itemPath] = true;
    Object.assign(result, childResult);
  }
  return result;
};

const Sidebar = ({ setCollapsedFromSidebar, menu, title, isCollapsed }) => {
  const collapsed = isCollapsed ?? false;
  const location = useLocation();
  const { t } = useTranslation();

  // On first render, auto-open dropdowns that contain the active route
  const [open, setOpen] = useState(() =>
    getInitialOpen(menu, location.pathname),
  );

  const toggle = () => setCollapsedFromSidebar(!collapsed);

  const toggleDropdown = (itemPath) => {
    setOpen((prev) => {
      const isCurrentlyOpen = prev[itemPath];

      if (isCurrentlyOpen) {
        // Closing: close this item and all its descendants
        const next = { ...prev };
        next[itemPath] = false;
        Object.keys(next).forEach((k) => {
          if (k.startsWith(itemPath + "/")) next[k] = false;
        });
        return next;
      }

      // Opening: build the set of ancestor paths that must stay open
      const ancestors = new Set();
      const parts = itemPath.split("/");
      for (let i = 1; i < parts.length; i++) {
        ancestors.add(parts.slice(0, i).join("/"));
      }

      // Start fresh — keep only ancestors open, then open this item
      const next = {};
      ancestors.forEach((a) => {
        next[a] = true;
      });
      next[itemPath] = true;
      return next;
    });
  };

  const renderItem = (item, level = 1, parentPath = "") => {
    if (item.type === "link") {
      return (
        <NavLink
          to={item.to}
          className={level === 1 ? "menu-item" : "submenu-item"}
          onMouseEnter={(e) => collapsed && showTooltip(e, item.text)}
          onMouseLeave={(e) => collapsed && hideTooltip(e)}
          onClick={() => {
            if (window.innerWidth <= 991 && !collapsed) {
              setCollapsedFromSidebar(true);
            }
          }}
        >
          <span className={level === 1 ? "menu-icon" : "submenu-icon"}>
            {item.icon}
          </span>
          {!collapsed && (
            <span className={level === 1 ? "menu-text" : "submenu-text"}>
              {item.text}
            </span>
          )}
        </NavLink>
      );
    }

    if (item.type === "dropdown") {
      const itemPath = parentPath ? `${parentPath}/${item.text}` : item.text;
      const isOpen = !!open[itemPath];

      return (
        <div className="menu-group">
          <button
            className={`menu-item dropdown-trigger ${
              isOpen ? "dropdown-activeaa" : ""
            }`}
            onClick={() => toggleDropdown(itemPath)}
            onMouseEnter={(e) => collapsed && showTooltip(e, item.text)}
            onMouseLeave={(e) => collapsed && hideTooltip(e)}
          >
            {item.icon && <span className="menu-icon">{item.icon}</span>}
            {!collapsed && (
              <>
                <span className="menu-text">{item.text}</span>
                <FaChevronRight
                  className={`dropdown-arrow ${isOpen ? "rotated" : ""}`}
                />
              </>
            )}
          </button>

          <div
            className={`dropdown-content ${isOpen ? "open" : "closed"}`}
            style={{ justifyItems: "end" }}
          >
            {!collapsed &&
              item.children?.map((child, index) => (
                <div key={index} style={{ width: "90%" }}>
                  {renderItem(child, level + 1, itemPath)}
                </div>
              ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className={`sidebar ${collapsed ? "collapsed" : "expanded"}`}>
        <div className="sidebar-header">
          <NavLink to="/dashboard" className="logo-link-vertical">
            <img src={LogoIcon} alt="Logo" className="s-logo" />
            {!collapsed && (
              <div className="sidebar-title-block">
                <span className="logo-text">{title}</span>
                <span className="logo-subtext">{t("dashboard")}</span>
              </div>
            )}
          </NavLink>
          <button className="sidebar-toggle" onClick={toggle}>
            <IoMdMenu />
          </button>
          {!collapsed && (
            <button className="sidebar-close-btn" onClick={toggle}>
              <IoMdClose />
            </button>
          )}
        </div>

        <div className="sidebar-menu">
          <div className="menu-section">
            {menu.map((item, index) => (
              <div key={index}>{renderItem(item)}</div>
            ))}
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="footer-content">
            {!collapsed && (
              <>
                <div className="copyright">
                  © {new Date().getFullYear()} {t("aeonian-technology")}
                </div>
                <Link to="/privacy-policy" className="privacy-link">
                  {t("privacy")} {t("policy")}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
